import React, { useEffect, useState } from 'react';
import { X, Download, Printer, QrCode, Loader2 } from 'lucide-react';
import QRCode from 'qrcode';

/**
 * TownQRModal — Iter 203
 *
 * Client-only QR generator for a town's Safety Alerts page. Reuses the
 * same `qrcode` library + branded-poster popup pattern that
 * `BusinessQRModal` proved out in Iter 197. The QR encodes
 * `${origin}/safety-bulletin?town={town}` so anyone who scans it lands
 * straight on the town's alerts feed and can hit the inline "Follow {town}"
 * button to start receiving notifications.
 *
 * Admin-only entry point — surfaced from the Admin "Safety" tab so the
 * platform team can generate posters on demand and hand them to Town
 * Halls / Guardia Civil / Community Partners.
 */
const BRAND_AMBER = '#b45309';

const TownQRModal = ({ open, onClose, town }) => {
  const [dataUrl, setDataUrl] = useState('');
  const [generating, setGenerating] = useState(true);

  const url = town
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/safety-bulletin?town=${encodeURIComponent(town)}`
    : '';

  useEffect(() => {
    if (!open || !url) return;
    let cancelled = false;
    setGenerating(true);
    QRCode.toDataURL(url, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 512,
      color: { dark: '#111827', light: '#ffffff' },
    }).then((d) => {
      if (cancelled) return;
      setDataUrl(d);
      setGenerating(false);
    }).catch(() => {
      if (!cancelled) setGenerating(false);
    });
    return () => { cancelled = true; };
  }, [open, url]);

  if (!open) return null;

  const townSafe = town || '';
  const slug = townSafe.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'town';
  const fileName = `find-2-day-safety-alerts-${slug}.png`;

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePrint = () => {
    if (!dataUrl) return;
    const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Follow ${escapeHtml(townSafe)} — Find-2-Day Safety Alerts</title>
  <style>
    @page { size: A5 portrait; margin: 0; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; padding: 0; background: #ffffff; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111827; }
    .sheet {
      width: 148mm; min-height: 210mm; margin: 0 auto; padding: 18mm 14mm; text-align: center;
      display: flex; flex-direction: column; justify-content: space-between; align-items: center;
    }
    .brand { font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase; color: ${BRAND_AMBER}; font-weight: 800; margin: 0; }
    h1 { font-size: 30px; line-height: 1.15; margin: 12px 0 4px; }
    .town { font-size: 22px; color: ${BRAND_AMBER}; font-weight: 800; margin: 4px 0 18px; }
    .qr-wrap { display: flex; align-items: center; justify-content: center; padding: 14px; border: 3px solid ${BRAND_AMBER}; border-radius: 16px; background: white; margin: 8px 0 20px; }
    .qr-wrap img { width: 240px; height: 240px; display: block; }
    .cta { font-size: 16px; color: #1f2937; line-height: 1.45; font-weight: 600; margin: 0 0 10px; }
    .lead { font-size: 13px; color: #4b5563; line-height: 1.5; margin: 0; max-width: 90%; }
    .footer { font-size: 11px; color: #9ca3af; margin-top: 18px; }
    .footer strong { color: ${BRAND_AMBER}; }
    .actions { margin-top: 14px; }
    .actions button { padding: 8px 14px; border: 1px solid ${BRAND_AMBER}; background: ${BRAND_AMBER}; color: #fff; border-radius: 6px; font-size: 13px; cursor: pointer; }
    @media print { .actions { display: none !important; } }
  </style>
</head>
<body>
  <div class="sheet">
    <div>
      <p class="brand">Find-2-Day · Community Safety Hub</p>
      <h1>Stay informed.</h1>
      <p class="town">Follow ${escapeHtml(townSafe)}</p>
      <p class="cta">Scan this code to receive official safety alerts from your Town Hall and Police on your phone.</p>
    </div>
    <div class="qr-wrap"><img id="qr-img" src="${dataUrl}" alt="QR code" /></div>
    <div>
      <p class="lead">Road closures · Water cut-offs · Weather warnings · Scam alerts · Missing persons. Free for all residents — no email required.</p>
      <p class="footer">Powered by <strong>Find-2-Day</strong> · find-2-day.com</p>
      <div class="actions"><button type="button" onclick="window.print()">Print</button></div>
    </div>
  </div>
  <script>
    (function () {
      var img = document.getElementById('qr-img');
      function fire() { try { window.focus(); window.print(); } catch (e) {} }
      if (img && img.complete && img.naturalWidth > 0) setTimeout(fire, 250);
      else if (img) {
        img.addEventListener('load', function () { setTimeout(fire, 250); });
      }
    })();
  <\/script>
</body>
</html>`;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=620,height=860');
    if (!win) {
      alert('Please allow pop-ups for find-2-day.com to print the town QR poster.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="town-qr-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-white" />
            <h2 className="text-white font-semibold text-base">Town QR code · {townSafe}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 -mr-1"
            data-testid="town-qr-close-btn"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="flex items-center justify-center bg-amber-50 rounded-xl p-4 border border-amber-200">
            {generating || !dataUrl ? (
              <div className="h-60 w-60 flex items-center justify-center text-gray-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <img
                src={dataUrl}
                alt={`QR code for ${townSafe} safety alerts`}
                className="h-60 w-60 rounded"
                data-testid="town-qr-image"
              />
            )}
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Direct URL</p>
            <p className="mt-1 text-sm text-amber-700 font-mono truncate" title={url} data-testid="town-qr-url">{url}</p>
          </div>

          <p className="text-xs text-gray-500 leading-relaxed">
            Hand the printed poster to the Town Hall, Police, or any Community Partner so residents can scan and start following their town in seconds.
          </p>

          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!dataUrl}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50"
              data-testid="town-qr-download-btn"
            >
              <Download className="h-4 w-4" /> Download PNG
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!dataUrl}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-white border border-amber-300 text-amber-700 rounded-lg text-sm font-semibold hover:bg-amber-50 transition-colors disabled:opacity-50"
              data-testid="town-qr-print-btn"
            >
              <Printer className="h-4 w-4" /> Print poster
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export default TownQRModal;
